import { PrimaryPointRenderer } from './primary-points';
import { PlanetCamera, projectionScale } from './planet-camera';
import type { RendererState } from './renderer-state';

export interface RenderInput {
  readonly deltaSeconds: number;
  readonly interpolation: number;
}

export class CosmicRenderer {
  public readonly gl: WebGL2RenderingContext;
  private readonly primary: PrimaryPointRenderer;
  private readonly state: RendererState;
  private readonly camera = new PlanetCamera();
  private internalPixelRatio = 1;
  private cssWidth = 1;
  private cssHeight = 1;
  private lastInterpolation = 1;

  public constructor(
    private readonly canvas: HTMLCanvasElement,
    state: RendererState,
  ) {
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      desynchronized: true,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    if (gl === null) {
      throw new Error('WebGL2 is unavailable.');
    }
    this.gl = gl;
    this.state = state;
    this.primary = new PrimaryPointRenderer(gl, state.capacity);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
  }

  public updatePrimary(): void {
    this.primary.update(this.state);
  }

  public resize(cssWidth: number, cssHeight: number): void {
    this.cssWidth = Math.max(1, cssWidth);
    this.cssHeight = Math.max(1, cssHeight);
    const deviceRatio = Math.min(window.devicePixelRatio || 1, 1.6);
    this.internalPixelRatio = Math.max(
      0.55,
      Math.min(1.6, deviceRatio),
    );
    const width = Math.max(1, Math.round(this.cssWidth * this.internalPixelRatio));
    const height = Math.max(
      1,
      Math.round(this.cssHeight * this.internalPixelRatio),
    );
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
  }

  public render(input: RenderInput): void {
    const gl = this.gl;
    this.lastInterpolation = input.interpolation;
    const camera = this.camera.update(
      input.deltaSeconds,
      this.state,
      input.interpolation,
    );
    const pointScale = Math.max(0.75, this.internalPixelRatio);
    const projection = projectionScale(this.cssWidth, this.cssHeight);
    const worldPointScale =
      Math.min(this.cssWidth, this.cssHeight) * this.internalPixelRatio;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.disable(gl.BLEND);
    gl.clearColor(0.0078, 0.0196, 0.0353, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    this.drawOrganisms(
      input.interpolation,
      pointScale,
      worldPointScale,
      projection,
      camera,
    );

    // Celestial bodies stay crisp and readable above the organisms.
    // The point shader emits premultiplied color, so this is premultiplied alpha.
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    this.primary.drawRange(
      input.interpolation,
      pointScale,
      worldPointScale,
      projection.x,
      projection.y,
      camera.centerX,
      camera.centerY,
      camera.zoom,
      0,
      this.state.celestialCount,
    );
  }

  private drawOrganisms(
    interpolation: number,
    pointScale: number,
    worldPointScale: number,
    projection: { readonly x: number; readonly y: number },
    camera: { readonly centerX: number; readonly centerY: number; readonly zoom: number },
  ): void {
    this.primary.drawRange(
      interpolation,
      pointScale,
      worldPointScale,
      projection.x,
      projection.y,
      camera.centerX,
      camera.centerY,
      camera.zoom,
      this.state.celestialCount,
      Math.max(0, this.state.count - this.state.celestialCount),
    );
  }

  public get renderPath(): 'direct' {
    return 'direct';
  }

  public toggleCameraAt(clientX: number, clientY: number): boolean {
    const bounds = this.canvas.getBoundingClientRect();
    const clipX = ((clientX - bounds.left) / Math.max(1, bounds.width)) * 2 - 1;
    const clipY = 1 - ((clientY - bounds.top) / Math.max(1, bounds.height)) * 2;
    return this.camera.toggleAt(
      clipX,
      clipY,
      bounds.width,
      bounds.height,
      this.state,
      this.lastInterpolation,
    );
  }

  public planetScreenPosition(index: number, offsetX = 0, offsetY = 0): {
    readonly x: number;
    readonly y: number;
  } | null {
    if (index < 0 || index >= this.state.count) {
      return null;
    }
    const blend = Math.max(0, Math.min(1, this.lastInterpolation));
    const worldX =
      (this.state.previousX[index] ?? 0) * (1 - blend) +
      (this.state.currentX[index] ?? 0) * blend;
    const worldY =
      (this.state.previousY[index] ?? 0) * (1 - blend) +
      (this.state.currentY[index] ?? 0) * blend;
    return this.worldScreenPosition(worldX + offsetX, worldY + offsetY);
  }

  public worldScreenPosition(
    worldX: number,
    worldY: number,
  ): { readonly x: number; readonly y: number } {
    const camera = this.camera.currentFrame;
    const projection = projectionScale(this.cssWidth, this.cssHeight);
    return {
      x:
        ((worldX - camera.centerX) * camera.zoom * projection.x + 1) *
        this.cssWidth *
        0.5,
      y:
        (1 - (worldY - camera.centerY) * camera.zoom * projection.y) *
        this.cssHeight *
        0.5,
    };
  }

  public get cameraMode(): string {
    return this.camera.mode;
  }

  public get cameraZoom(): number {
    return this.camera.currentFrame.zoom;
  }

  public zoomCamera(deltaY: number): void {
    this.camera.zoomByWheel(deltaY);
  }

  public pinchCamera(ratio: number, fromX: number, fromY: number, toX: number, toY: number): void {
    const rect = this.canvas.getBoundingClientRect();
    this.camera.pinchByPixels(ratio, fromX - rect.left, fromY - rect.top,
      toX - rect.left, toY - rect.top, this.cssWidth, this.cssHeight);
  }

  public panCamera(deltaX: number, deltaY: number): void {
    this.camera.panByPixels(
      deltaX,
      deltaY,
      this.cssWidth,
      this.cssHeight,
    );
  }

  public get cameraCenterX(): number {
    return this.camera.currentFrame.centerX;
  }

  public get cameraCenterY(): number {
    return this.camera.currentFrame.centerY;
  }

  public resetCamera(): void {
    this.camera.reset();
  }

  public dispose(): void {
    this.primary.dispose();
  }
}
